<template>
  <v-dialog
    :model-value="open"
    max-width="480"
    class="gr-locdialog gr-overlay"
    @update:model-value="$emit('close')"
  >
    <div class="gr-locdialog__card">
      <header class="gr-locdialog__head">
        <h2 class="gr-locdialog__title">Filter Lokasi</h2>
        <button
          type="button"
          class="gr-locdialog__close"
          aria-label="Tutup filter lokasi"
          @click="onClose"
        >
          <v-icon icon="mdi-close" size="20" aria-hidden="true" />
        </button>
      </header>

      <div class="gr-locdialog__body">
        <p class="gr-locdialog__label">Lokasi</p>

        <v-autocomplete
          v-model="selectedProvince"
          label="Provinsi"
          density="comfortable"
          variant="outlined"
          :items="provinces"
          item-title="name"
          item-value="id"
          :loading="loadingProvinces"
          clearable
          hide-details
          class="mb-4"
          @update:model-value="handleProvinceChange"
        />

        <v-autocomplete
          v-model="selectedCity"
          label="Kota/Kabupaten"
          density="comfortable"
          variant="outlined"
          :items="cities"
          item-title="name"
          item-value="id"
          :loading="loadingCities"
          :disabled="!selectedProvince"
          clearable
          hide-details
          class="mb-4"
        />

        <p v-if="errorMessage" class="gr-locdialog__error" role="alert">
          <v-icon
            icon="mdi-alert-circle-outline"
            size="18"
            aria-hidden="true"
          />
          {{ errorMessage }}
        </p>
      </div>

      <footer class="gr-locdialog__actions">
        <button type="button" class="gr-btn gr-btn--quiet" @click="onClose">
          Batal
        </button>
        <button type="button" class="gr-btn gr-btn--quiet" @click="resetFilter">
          Reset
        </button>
        <button
          type="button"
          class="gr-btn gr-btn--solid"
          @click="handleApplyFilter"
        >
          Terapkan
        </button>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import {
  useRegions,
  type Province,
  type City,
} from "~/common/composables/useRegions";

interface LocationFilter {
  province?: number;
  city?: number;
}

const props = withDefaults(
  defineProps<{ open: boolean; filter: LocationFilter }>(),
  { open: false, filter: () => ({ province: undefined, city: undefined }) },
);

const emit = defineEmits<{ close: []; apply: [value: LocationFilter] }>();

// Stateless read-only composable, reused rather than reimplemented.
const { getProvinces, getCities } = useRegions();

const provinces = ref<Province[]>([]);
const cities = ref<City[]>([]);
const selectedProvince = ref<number | null>(props.filter.province || null);
const selectedCity = ref<number | null>(props.filter.city || null);
const loadingProvinces = ref(false);
const loadingCities = ref(false);
const errorMessage = ref<string | null>(null);

// Canonical logs region failures to the console, where a visitor never sees
// them and the dialog just sits empty. Surfacing them in the dialog's own
// error line says what happened without a technical message.
async function loadProvinces(): Promise<void> {
  loadingProvinces.value = true;
  try {
    provinces.value = await getProvinces();
  } catch {
    errorMessage.value =
      "Daftar provinsi gagal dimuat. Coba tutup dan buka lagi filter ini.";
  } finally {
    loadingProvinces.value = false;
  }
}

async function loadCities(provinceId: number): Promise<void> {
  loadingCities.value = true;
  cities.value = [];
  try {
    cities.value = await getCities(provinceId);
  } catch {
    errorMessage.value =
      "Daftar kota gagal dimuat. Pilih provinsi lagi untuk mencoba ulang.";
  } finally {
    loadingCities.value = false;
  }
}

onMounted(async () => {
  await loadProvinces();
  if (selectedProvince.value) await loadCities(selectedProvince.value);
});

watch(
  () => props.open,
  async (isOpen) => {
    if (!isOpen) return;
    selectedProvince.value = props.filter.province || null;
    selectedCity.value = props.filter.city || null;
    if (provinces.value.length === 0) await loadProvinces();
    if (selectedProvince.value && cities.value.length === 0) {
      await loadCities(selectedProvince.value);
    }
  },
);

async function handleProvinceChange(provinceId: number | null): Promise<void> {
  errorMessage.value = null;
  selectedCity.value = null;
  cities.value = [];
  if (provinceId) await loadCities(provinceId);
}

function onClose(): void {
  errorMessage.value = null;
  emit("close");
}

function handleApplyFilter(): void {
  errorMessage.value = null;
  // A province on its own is not a filter the API accepts, so the dialog holds
  // the visitor here rather than applying half a location.
  if (selectedProvince.value && !selectedCity.value) {
    errorMessage.value = "Pilih kota atau kabupaten terlebih dahulu.";
    return;
  }

  emit("apply", {
    province: selectedProvince.value || undefined,
    city: selectedCity.value || undefined,
  });
  emit("close");
}

function resetFilter(): void {
  errorMessage.value = null;
  selectedProvince.value = null;
  selectedCity.value = null;
  cities.value = [];
  handleApplyFilter();
}
</script>

<style scoped>
.gr-locdialog__card {
  border-radius: 4px;
  background-color: var(--gr-surface);
  color: var(--gr-ink);
}

.gr-locdialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--gr-line);
}

.gr-locdialog__title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.3;
}

.gr-locdialog__close {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  margin: -10px -10px -10px 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--gr-ink-2);
  cursor: pointer;
}

.gr-locdialog__body {
  padding: 20px;
}

.gr-locdialog__label {
  margin: 0 0 14px;
  color: var(--gr-ink-3);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  line-height: 1;
  text-transform: uppercase;
}

.gr-locdialog__error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--gr-danger);
  font-size: 0.875rem;
  line-height: 1.5;
}

.gr-locdialog__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px 20px;
  border-top: 1px solid var(--gr-line);
}
</style>
